<template>
  <div
    class="data-table-container"
    :class="{ 'has-height': !!height }"
    :style="height ? `--table-height: ${height}` : undefined"
  >
    <div
      v-if="(visibleColumns && showColumnToggles) || (showRowFilters && rowFilterOptions.length > 0)"
      class="table-controls"
    >
      <DropdownMenu
        v-if="visibleColumns && showColumnToggles"
        position="bottom-left"
      >
        <template #trigger="{ toggle }">
          <BaseButton
            color="neutral"
            icon="gear"
            square
            variant="outline"
            @click="toggle"
          />
        </template>
        <template #content>
          <div class="controls-dropdown-list">
            <BaseText> Toggle columns: </BaseText>
            <FormControl
              v-for="column in columns.filter(col => col.label && col.label.trim() !== '')"
              :key="column.key"
              :label="column.label"
              :model-value="userVisibleColumns.has(String(column.key)) ? 'true' : 'false'"
              type="checkbox"
              @update:model-value="() => toggleColumnVisibility(String(column.key))"
            />
          </div>
        </template>
      </DropdownMenu>

      <DropdownMenu
        v-if="showRowFilters && rowFilterOptions.length > 0"
        position="bottom-left"
      >
        <template #trigger="{ toggle }">
          <BaseButton
            color="neutral"
            icon="funnel"
            square
            variant="outline"
            @click="toggle"
          />
        </template>
        <template #content>
          <div class="controls-dropdown-list">
            <BaseText> Filter rows: </BaseText>
            <FormControl
              v-for="value in rowFilterOptions"
              :key="value"
              :label="rowFilterFormatter ? rowFilterFormatter(value) : value"
              :model-value="userVisibleRowValues.has(value) ? 'true' : 'false'"
              type="checkbox"
              @update:model-value="() => toggleRowFilter(value)"
            />
          </div>
        </template>
      </DropdownMenu>
    </div>
    <table
      :aria-busy="isLoading"
      :aria-label="ariaLabel || 'Data table'"
      :aria-rowcount="displayedRows.length + 1"
      class="data-table"
      :class="[{ fixed: fixedColumns }, size]"
      role="table"
    >
      <thead>
        <tr role="row">
          <th
            v-for="(column, index) in displayedColumns"
            :key="column.key"
            :aria-sort="getAriaSortValue(column)"
            :class="[
              column.class,
              column.sortable ? 'sortable' : '',
              column.align,
              { 'sort-active': sortBy === (column.sortKey || column.key) },
              { wrappable: column.wrappable },
            ]"
            role="columnheader"
            :scope="column.scope || 'col'"
            :style="{ '--col-width': getColumnWidth(index) }"
            :tabindex="column.sortable ? 0 : undefined"
            @click="column.sortable ? handleSort(column) : null"
            @keydown="column.sortable ? handleSortKeydown(column, $event) : null"
          >
            {{ column.label }}
            <span
              v-if="column.sortable"
              class="sort-icon"
            >
              <BaseIcon
                v-if="isSortedAsc(column)"
                id="caret-up"
                size="sm"
              />
              <BaseIcon
                v-else-if="isSortedDesc(column)"
                id="caret-down"
                size="sm"
              />
              <BaseIcon
                v-else
                id="caret-up-down"
                size="sm"
              />
            </span>
          </th>
        </tr>
      </thead>

      <tbody
        v-if="isLoading"
        aria-label="Loading data"
        aria-live="polite"
      >
        <tr role="row">
          <td
            aria-live="polite"
            class="loading-cell"
            :colspan="displayedColumns.length"
            role="cell"
          >
            <div class="loading-content">
              <div
                v-if="!$slots.loading"
                aria-label="Loading table data"
                class="default-loading"
                role="status"
              >
                <BaseIcon
                  id="spinner"
                  inline
                />
                Loading data...
              </div>
              <slot name="loading" />
            </div>
          </td>
        </tr>
      </tbody>

      <tbody
        v-else-if="error"
        aria-label="Error occurred"
        aria-live="assertive"
      >
        <tr role="row">
          <td
            aria-live="assertive"
            class="error-cell"
            :colspan="displayedColumns.length"
            role="cell"
          >
            <div class="error-content">
              <div
                v-if="!$slots.error"
                :aria-label="`Error: ${getErrorMessage(error)}`"
                class="default-error"
                role="alert"
              >
                <BaseIcon
                  id="warning"
                  inline
                />
                <span>{{ getErrorMessage(error) }}</span>
              </div>
              <slot
                :error="error"
                :error-message="getErrorMessage(error)"
                name="error"
              />
            </div>
          </td>
        </tr>
      </tbody>

      <tbody
        v-else-if="displayedRows.length === 0"
        aria-live="polite"
      >
        <tr role="row">
          <td
            class="empty-cell wrappable"
            :colspan="displayedColumns.length"
            role="cell"
          >
            <div class="empty-content">
              <div
                v-if="!$slots.empty"
                aria-label="No data available"
                class="default-empty"
                role="status"
              >
                <BaseIcon
                  id="info"
                  inline
                />
                <span>No results found.</span>
              </div>
              <slot name="empty" />
            </div>
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <template
          v-for="(row, rowIndex) in displayedRows"
          :key="getRowKey(row, rowIndex)"
        >
          <tr
            :aria-rowindex="rowIndex + 2"
            :class="{ clickable: !!rowClickable }"
            role="row"
            :tabindex="rowClickable ? 0 : undefined"
            @click="handleRowClick(row, $event)"
            @keydown="rowClickable ? handleRowKeydown(row, $event) : null"
          >
            <td
              v-for="column in displayedColumns"
              :key="column.key"
              :class="[column.class, column.align, { wrappable: column.wrappable }]"
              :data-testid="column.key"
              role="cell"
            >
              <button
                v-if="rowExpandable && column.key === 'toggle'"
                class="toggle-button"
                :class="{ active: isRowExpanded(getRowKey(row, rowIndex)) }"
                @click="toggleRowExpand(getRowKey(row, rowIndex), $event)"
              >
                <BaseIcon
                  id="caret-down"
                  size="sm"
                />
              </button>
              <slot
                v-else
                :column="column"
                :name="column.key"
                :row="row"
                :value="getColumnValue(row, column)"
              >
                <component
                  :is="column.component"
                  v-if="column.component"
                  v-bind="getComponentProps(row, column)"
                />
                <span v-else>{{ formatValue(getColumnValue(row, column), column.format) }}</span>
              </slot>
            </td>
          </tr>

          <tr
            v-if="rowExpandable && isRowExpanded(getRowKey(row, rowIndex))"
            class="expanded-row"
          >
            <td :colspan="displayedColumns.length">
              <slot
                name="expanded-content"
                :row="row"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import type { Component } from 'vue';
import { computed, ref, shallowRef, triggerRef, watch } from 'vue';

import BaseButton from '../atoms/BaseButton.vue';
import BaseIcon from '../atoms/BaseIcon.vue';
import BaseText from '../atoms/BaseText.vue';
import DropdownMenu from '../layouts/overlays/DropdownMenu.vue';
import FormControl from '../molecules/FormControl.vue';

/**
 * Configuration interface for DataTable columns
 * @template T The type of data objects in the table rows
 */
export interface TableColumn<T = Record<string, unknown>> {
  /**
   * The property key to access data from row objects, or a string identifier for the column
   */
  key: keyof T | string;

  /**
   * Display label for the column header
   */
  label: string;

  /**
   * Custom path to access nested data from row objects
   * Can be a single key, string path, or array of keys for deep nesting
   */
  path?: keyof T | string | (keyof T | string)[];

  /**
   * HTML scope attribute for table header (th) elements
   * @default 'col'
   */
  scope?: string;

  /**
   * CSS classes to apply to the column header and cells
   */
  class?: string | string[];

  /**
   * Function to format cell values for display
   * Receives the raw value and should return a formatted string or number
   */
  format?: (value: unknown) => string | number;

  /**
   * Vue component to render in the cell instead of formatted text
   */
  component?: Component;

  /**
   * Props to pass to the component
   * Can be a static object or a function that receives the row data
   */
  componentProps?: Record<string, unknown> | ((row: T) => Record<string, unknown>);

  /**
   * Whether this column supports sorting
   * @default false
   */
  sortable?: boolean;

  /**
   * Custom sort key if different from the column key
   * Useful when the display key differs from the sort property
   */
  sortKey?: string;

  /**
   * CSS width value for the column
   * @example '100px' | '20%' | 'auto'
   */
  width?: string;

  /**
   * Text alignment for the column content
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right';

  /**
   * Allow text wrapping in cells for this column
   * @default false
   */
  wrappable?: boolean;
}

/**
 * Props interface for DataTable component
 * @template T The type of data objects in the table rows
 */
interface Props<T = Record<string, unknown>> {
  /**
   * Accessible label for the table
   * @default 'Data table'
   */
  ariaLabel?: string;

  /**
   * Array of column configuration objects defining table structure
   */
  columns: TableColumn<T>[];

  /**
   * Array of data objects to display as table rows
   */
  rows: T[];

  /**
   * Property to use as unique row identifier, or function to generate unique keys
   * If not provided, tries common keys (id, key) or falls back to array index
   * @default undefined (auto-detect)
   */
  rowKey?: keyof T | ((row: T, index: number) => string);

  /**
   * Whether the table is in loading state
   * Shows loading content in place of table body when true
   * @default false
   */
  isLoading?: boolean;

  /**
   * Error message or Error object to display
   * Shows error content in place of table body when present
   * @default null
   */
  error?: string | Error | null;

  /**
   * Whether rows can be expanded to show additional content
   * Requires a toggle column and expanded-content slot
   * @default false
   */
  rowExpandable?: boolean;

  /**
   * Whether rows are clickable and emit row-click events
   * Adds hover effects and cursor pointer styling
   * @default false
   */
  rowClickable?: boolean;

  /**
   * Current sort column key for displaying sort indicators
   * Should match a column's key or sortKey property
   */
  sortBy?: string;

  /**
   * Current sort direction for displaying sort indicators
   * @default 'asc'
   */
  sortOrder?: string;

  /**
   * Whether to use fixed table layout for consistent column widths
   * Useful when column widths are specified
   * @default false
   */
  fixedColumns?: boolean;

  /**
   * Size variant of the table affecting padding and font sizes
   * - null: Standard sizing
   * - 'sm': Compact sizing for dense layouts
   * @default null
   */
  size?: 'sm' | null;

  /**
   * CSS height value for creating a scrollable table
   * When set, table body becomes scrollable with fixed height
   * @example '400px' | '50vh'
   */
  height?: string;

  /**
   * Array of column keys that should be visible
   * Columns not in this array will be hidden (if filtering is enabled)
   */
  visibleColumns?: string[];

  /**
   * Whether to show column visibility toggle controls
   * Displays a gear icon that opens a dropdown with checkboxes for each column
   * @default false
   */
  showColumnToggles?: boolean;

  /**
   * Property key to filter rows by
   * When set, shows a filter dropdown with unique values from this column
   */
  rowFilterKey?: string;

  /**
   * Whether to show row filtering controls
   * Displays a filter icon that opens a dropdown with checkboxes for each unique value
   * @default false
   */
  showRowFilters?: boolean;

  /**
   * Function to format row filter values for display
   * Receives a raw value and returns a formatted string
   * @example (value) => value.charAt(0).toUpperCase() + value.slice(1)
   */
  rowFilterFormatter?: (value: string) => string;
}

const {
  ariaLabel = undefined,
  columns,
  rows,
  rowKey = undefined,
  isLoading = false,
  error = null,
  rowExpandable = false,
  rowClickable = false,
  sortBy = '',
  sortOrder = 'asc',
  fixedColumns = false,
  size = null,
  height = undefined,
  visibleColumns = undefined,
  showColumnToggles = false,
  rowFilterKey = undefined,
  showRowFilters = false,
  rowFilterFormatter = undefined,
} = defineProps<Props>();

const expandedRows = ref(new Set<string>());
const userVisibleColumns = shallowRef<Set<string>>(new Set());
const userVisibleRowValues = shallowRef<Set<string>>(new Set());

const initializeVisibleColumns = () => {
  if (visibleColumns) {
    userVisibleColumns.value = new Set(visibleColumns);
  } else {
    userVisibleColumns.value = new Set(columns.map(col => String(col.key)));
  }
};

const initializeVisibleRowValues = () => {
  if (rowFilterKey) {
    userVisibleRowValues.value = new Set();
  }
};

initializeVisibleColumns();

watch(
  () => columns,
  () => {
    initializeVisibleColumns();
  },
);

const displayedColumns = computed(() => {
  if (!visibleColumns) {
    return columns;
  }

  const filterColumns = (cols: typeof columns) => {
    return cols.filter(col => {
      if (!col.label || col.label.trim() === '') {
        return true;
      }

      const columnKey = String(col.key);

      if (showColumnToggles) {
        return userVisibleColumns.value.has(columnKey);
      }

      return visibleColumns.includes(columnKey);
    });
  };

  return filterColumns(columns);
});

const rowFilterOptions = computed(() => {
  if (!rowFilterKey) return [];

  const values = new Set<string>();
  rows.forEach(row => {
    values.add(String(row[rowFilterKey] ?? 'Other'));
  });
  return Array.from(values);
});

const displayedRows = computed(() => {
  if (!rowFilterKey || userVisibleRowValues.value.size === 0) {
    return rows;
  }

  return rows.filter(row => userVisibleRowValues.value.has(String(row[rowFilterKey] ?? 'Other')));
});

initializeVisibleRowValues();

watch(
  () => [rows, rowFilterKey],
  () => {
    initializeVisibleRowValues();
  },
);

/**
 * Events emitted by the DataTable component
 */
interface Emits {
  /**
   * Emitted when a clickable row is clicked
   * @param row The data object for the clicked row
   * @param openInNewTab Whether the click should open in a new tab (cmd/ctrl was pressed)
   */
  'row-click': [row: Record<string, unknown>, openInNewTab: boolean];

  /**
   * Emitted when column sorting changes
   * @param payload Object containing the new sort column and direction
   */
  'sort-changed': [{ sortBy: string; sortOrder: string }];

  /**
   * Emitted when column visibility is toggled
   * @param payload Object containing visibility change details
   */
  'column-visibility-changed': [{ columnKey: string; visible: boolean; visibleColumns: string[] }];
}

const emits = defineEmits<Emits>();

/**
 * Toggles the visibility of a column and emits the change event
 * @param columnKey The key of the column to toggle
 */
const toggleColumnVisibility = (columnKey: string) => {
  const newVisible = !userVisibleColumns.value.has(columnKey);

  if (newVisible) {
    userVisibleColumns.value.add(columnKey);
  } else {
    userVisibleColumns.value.delete(columnKey);
  }

  triggerRef(userVisibleColumns);

  emits('column-visibility-changed', {
    columnKey,
    visible: newVisible,
    visibleColumns: Array.from(userVisibleColumns.value),
  });
};

const toggleRowFilter = (value: string) => {
  if (userVisibleRowValues.value.has(value)) {
    userVisibleRowValues.value.delete(value);
  } else {
    userVisibleRowValues.value.add(value);
  }
  triggerRef(userVisibleRowValues);
};

const toggleRowExpand = (rowId: string, event: Event) => {
  event.stopPropagation();
  if (expandedRows.value.has(rowId)) {
    expandedRows.value.delete(rowId);
  } else {
    expandedRows.value.add(rowId);
  }
};

const isRowExpanded = (rowId: string) => {
  return expandedRows.value.has(rowId);
};

const getColumnWidth = (index: number): string => {
  const column = displayedColumns.value[index];
  const width = column?.width;

  if (!width) return 'auto';
  return typeof width === 'number' ? `${width}%` : width;
};

const getColumnValue = (row: Record<string, unknown>, column: TableColumn): unknown => {
  if (!row) return undefined;

  if (column.path) {
    if (Array.isArray(column.path)) {
      // Optimize nested path access
      let current: unknown = row;
      for (const path of column.path) {
        if (current && typeof current === 'object' && current !== null) {
          current = (current as Record<string, unknown>)[path];
        } else {
          return undefined;
        }
      }
      return current;
    } else {
      return row[column.path];
    }
  }

  return row[column.key];
};

const formatValue = (value: unknown, formatter?: (value: unknown) => string | number) => {
  if (formatter && typeof formatter === 'function') {
    return formatter(value);
  }
  return String(value ?? '');
};

const getComponentProps = (row: Record<string, unknown>, column: TableColumn) => {
  if (!column.componentProps) {
    return { value: getColumnValue(row, column) };
  }

  if (typeof column.componentProps === 'function') {
    return column.componentProps(row);
  }

  return column.componentProps;
};

const getRowKey = (row: Record<string, unknown>, index: number): string => {
  if (typeof rowKey === 'function') {
    return rowKey(row, index);
  }

  if (rowKey === undefined) {
    const key = row.id ?? row.key ?? index;
    return String(key);
  }

  const key = row[rowKey];
  return key !== undefined ? String(key) : String(index);
};

const handleRowClick = (row: Record<string, unknown>, event: MouseEvent | null = null) => {
  if (rowClickable) {
    const openInNewTab = event ? event.metaKey || event.ctrlKey : false;
    emits('row-click', row, openInNewTab);
  }
};

const isSortedAsc = (column: TableColumn) => {
  const sortKey = column.sortKey || column.key;
  return sortBy === sortKey && sortOrder === 'asc';
};

const isSortedDesc = (column: TableColumn) => {
  const sortKey = column.sortKey || column.key;
  return sortBy === sortKey && sortOrder === 'desc';
};

const handleSort = (column: TableColumn) => {
  if (!column.sortable) return;

  const sortKey = column.sortKey || column.key;
  let newSortOrder = 'asc';

  if (sortBy === sortKey) {
    newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
  }

  emits('sort-changed', { sortBy: sortKey, sortOrder: newSortOrder });
};

/**
 * Handles keyboard navigation for sortable columns
 */
const handleSortKeydown = (column: TableColumn, event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleSort(column);
  }
};

/**
 * Handles keyboard navigation for clickable rows
 */
const handleRowKeydown = (row: Record<string, unknown>, event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handleRowClick(row, null);
  }
};

/**
 * Gets the aria-sort value for column headers
 */
const getAriaSortValue = (column: TableColumn) => {
  if (!column.sortable) return undefined;

  const sortKey = column.sortKey || column.key;
  if (sortBy === sortKey) {
    return sortOrder === 'asc' ? 'ascending' : 'descending';
  }
  return 'none';
};

/**
 * Extracts error message from error prop
 */
const getErrorMessage = (error: string | Error | null): string => {
  if (!error) return 'An error occurred';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  return 'Unknown error';
};
</script>

<style>
.data-table-container {
  width: 100%;
  margin-bottom: var(--gap-md);
  position: relative;

  .table-controls {
    position: absolute;
    top: var(--border-radius-md);
    right: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--gap-xs);

    .dropdown-trigger {
      --button-border-radius-right: 0;
      --button-border-width-right: 0;
    }

    .dropdown-container {
      border-radius: var(--border-radius-md) 0 0 var(--border-radius-md);
      border: 1px solid var(--color-neutral-300);
      border-right: none;
      background-color: var(--color-light);
    }

    .controls-dropdown-list {
      display: flex;
      flex-direction: column;
      padding: var(--gap-xs);
    }
  }

  &.has-height {
    min-height: var(--table-height);
    overflow: auto;
    padding-right: var(--gap-xs);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    box-shadow: var(--box-shadow-down);
    border-radius: var(--border-radius-md);
    overflow: hidden;
    &.fixed {
      table-layout: fixed;
    }
    &.sm {
      border-radius: var(--border-radius-sm);
      td,
      th {
        font-size: var(--font-sm);
        padding: var(--gap-xs);
      }
    }
    td,
    th {
      transition: var(--transition-default);
      text-align: left;
      padding: var(--gap-sm) var(--gap-sm);
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow-x: hidden;
      width: var(--col-width, auto);
      font-size: var(--font-md);
      line-height: 1.5;

      &.wrappable {
        white-space: normal;
      }
      &.right {
        text-align: right;
        > div {
          margin-left: auto;
        }
      }
      &.center {
        text-align: center;
        > div {
          margin-left: auto;
          margin-right: auto;
        }
      }
      &:has(.dropdown-container) {
        overflow-x: visible;
      }
    }
    th {
      background-color: var(--table-header);
      color: var(--color-light);
      font-weight: var(--font-semi);
      &.sortable {
        cursor: pointer;
        user-select: none;
        position: relative;
        &.sort-active {
          background-color: var(--table-header-darker);
        }
      }
    }
    td {
      font-weight: var(--font-normal);
    }

    tbody tr {
      &:hover {
        background-color: var(--color-neutral-100);
      }
      &:nth-child(odd) td {
        background-color: var(--color-light);
      }
      &:nth-child(even) td {
        background-color: var(--color-neutral-100);
      }

      &.clickable {
        &:has(+ .expanded-row) td {
          td {
            white-space: wrap;
            word-break: break-word;
          }
        }
        &:has(+ .expanded-row) td,
        + .expanded-row td {
          background-color: var(--yellow-300);
        }
        &:hover td {
          background-color: var(--yellow-300);
        }
      }
    }
  }

  .toggle-button {
    border-radius: var(--border-radius-sm);
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1/1;
    border: 1px solid var(--color-neutral-300);
    background-color: transparent;
    transition: var(--transition-default);
    will-change: transform;
    &.active {
      transform: scaleY(-1);
    }
  }

  th.sortable:hover {
    background-color: var(--table-header-darker);
  }

  .sort-icon {
    display: inline-block;
    margin-left: 0.5rem;
    vertical-align: middle;
  }

  tbody tr:not(:last-child) {
    border-bottom: 1px solid var(--color-neutral-300);
  }

  tr.clickable {
    cursor: pointer;
  }

  /* Loading, error, and empty state styles */
  .loading-cell,
  .error-cell,
  .empty-cell {
    text-align: center;
    padding: var(--gap-lg);

    .default-loading,
    .default-error,
    .default-empty {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--gap-sm);
      font-weight: var(--font-md);
    }

    .default-error {
      color: var(--color-red-400);
    }

    .default-empty {
      color: var(--color-neutral-600);
    }

    &.wrappable {
      text-align: left;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Focus styles for accessibility */
  th.sortable:focus-visible,
  tr.clickable:focus-visible {
    outline: 2px solid var(--color-blue-300);
    outline-offset: 2px;
  }
}
</style>
